import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle, XCircle, Funnel, ArrowClockwise, DownloadSimple, Printer } from "@phosphor-icons/react";

const STATUSES = ["", "open", "paid", "dismissed"];

export default function Violations() {
    const { t } = useI18n();
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("");
    const [plate, setPlate] = useState("");
    const [busyRow, setBusyRow] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const q = new URLSearchParams();
            if (status) q.set("status", status);
            if (plate) q.set("plate", plate.trim().toUpperCase());
            const { data } = await api.get(`/violations?${q.toString()}`);
            setItems(data.violations || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error"));
        }
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

    const patch = async (v, newStatus) => {
        setBusyRow(v.id);
        try {
            await api.patch(`/violations/${v.id}/status`, { status: newStatus });
            setItems((x) => x.map((r) => r.id === v.id ? { ...r, status: newStatus } : r));
            toast.success(t(newStatus === "paid" ? "violationMarkedPaid" : "violationDismissed"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error"));
        }
        setBusyRow(null);
    };

    const totals = items.reduce((a, v) => {
        a[v.status] = (a[v.status] || 0) + 1;
        return a;
    }, {});

    return (
        <div className="max-w-6xl mx-auto space-y-6" data-testid="violations-page">
            <div>
                <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· CITATIONS</div>
                <h1 className="text-3xl font-bold tracking-tight mt-1">{t("violationsAdminTitle")}</h1>
                <p className="text-sm text-muted-foreground mt-1">{t("violationsAdminHint")}</p>
            </div>

            <Card className="p-4 rounded-sm border-border">
                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <div className="text-[10px] tracking-widest uppercase text-muted-foreground font-bold">{t("status")}</div>
                        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm mt-1" data-testid="viol-filter-status">
                            {STATUSES.map((s) => <option key={s} value={s}>{s || t("adminAll") || "All"}</option>)}
                        </select>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <div className="text-[10px] tracking-widest uppercase text-muted-foreground font-bold">{t("plateNumber")}</div>
                        <Input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} placeholder="DEMO-GRN-001" className="mono mt-1" data-testid="viol-filter-plate" />
                    </div>
                    <Button variant="outline" onClick={load} data-testid="viol-refresh"><ArrowClockwise size={14} weight="bold" className="me-1" />{t("refresh")}</Button>
                    <Button variant="outline" onClick={async () => {
                        try {
                            const q = new URLSearchParams();
                            if (status) q.set("status", status);
                            if (plate) q.set("plate", plate.trim().toUpperCase());
                            const res = await api.get(`/violations/export.csv?${q}`, { responseType: "blob" });
                            const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
                            const a = document.createElement("a");
                            a.href = url; a.download = "utal-violations.csv"; a.click();
                            URL.revokeObjectURL(url);
                            toast.success(t("exportSuccess"));
                        } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
                    }} data-testid="viol-export-csv"><DownloadSimple size={14} weight="bold" className="me-1" />CSV</Button>
                    <Button variant="outline" onClick={() => window.print()} data-testid="viol-print-pdf" className="print:hidden"><Printer size={14} weight="bold" className="me-1" />PDF</Button>
                    <div className="ms-auto flex gap-3 text-xs">
                        <span className="mono"><b className="text-primary">{totals.open || 0}</b> {t("statusOpen")}</span>
                        <span className="mono"><b className="text-green-700">{totals.paid || 0}</b> {t("statusPaid")}</span>
                        <span className="mono"><b className="text-muted-foreground">{totals.dismissed || 0}</b> {t("statusDismissed")}</span>
                    </div>
                </div>
            </Card>

            <Card className="rounded-sm border-border overflow-x-auto">
                {loading ? (
                    <div className="p-6 text-center text-muted-foreground text-sm">{t("loading")}</div>
                ) : items.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground text-sm" data-testid="viol-empty">{t("noViolations")}</div>
                ) : (
                    <table className="w-full data-grid text-sm">
                        <thead className="bg-secondary text-xs uppercase tracking-widest">
                            <tr>
                                <th className="text-start">{t("plateNumber")}</th>
                                <th className="text-start">{t("violationType")}</th>
                                <th className="text-end">{t("fineAmount")}</th>
                                <th className="text-start">{t("location")}</th>
                                <th className="text-start">{t("auditActor")}</th>
                                <th className="text-start">{t("auditTimestamp")}</th>
                                <th className="text-start">{t("status")}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((v) => (
                                <tr key={v.id} className="border-t border-border" data-testid={`viol-row-${v.id}`}>
                                    <td className="mono">{v.plate_number}</td>
                                    <td>{v.type_label}</td>
                                    <td className="text-end mono">{v.amount} {v.currency}</td>
                                    <td className="text-xs">{v.location || <span className="text-muted-foreground">—</span>}</td>
                                    <td className="text-xs">{v.officer_email}</td>
                                    <td className="text-xs mono">{new Date(v.issued_at).toLocaleString()}</td>
                                    <td>
                                        <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-widest font-bold ${
                                            v.status === "paid" ? "bg-green-100 text-green-900" :
                                            v.status === "dismissed" ? "bg-secondary text-muted-foreground" :
                                            "bg-primary text-primary-foreground"
                                        }`}>{v.status}</span>
                                    </td>
                                    <td className="text-end whitespace-nowrap">
                                        <Button size="sm" variant="ghost" onClick={async () => {
                                            try {
                                                const res = await api.get(`/violations/${v.id}/citation.pdf`, { responseType: "blob" });
                                                const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                                                window.open(url, "_blank");
                                            } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
                                        }} data-testid={`viol-pdf-${v.id}`}><Printer size={12} className="me-1" />PDF</Button>
                                        {v.status === "open" && (
                                            <>
                                                <Button size="sm" variant="ghost" disabled={busyRow === v.id} onClick={() => patch(v, "paid")} data-testid={`viol-paid-${v.id}`}>
                                                    <CheckCircle size={14} weight="fill" className="me-1 text-green-700" />{t("statusPaid")}
                                                </Button>
                                                <Button size="sm" variant="ghost" disabled={busyRow === v.id} onClick={() => patch(v, "dismissed")} data-testid={`viol-dismiss-${v.id}`}>
                                                    <XCircle size={14} weight="fill" className="me-1 text-muted-foreground" />{t("statusDismissed")}
                                                </Button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>
        </div>
    );
}
